import { useParams } from "react-router-dom";
import Slider from "react-slick";
import axios from "axios";
import Breadcrumb from "./Breadcrumb";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./Pages.css";
import { useQuery } from "@tanstack/react-query";
import Image from "../ui/Image";
import { useEffect } from "react";
import { useLanguage } from "../../store/language-context";
import { useTranslation } from "react-i18next";

const TrainingPrograms = () => {
  const { URL } = useLanguage();
  const { dependencyId } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dependency-details', dependencyId, URL],
    queryFn: async () => {
      try {
        const res = await axios.get(`${URL}/dependencies/${dependencyId}`);
        const result = await res?.data?.data;
        return result
      } catch (err) {
        throw new Error(t('custom.err'))
      }
    },
    staleTime: 1000 * 60 * 60 * 1,// 1HR
    revalidateOnMount: false,
    revalidateOnFocus: false,
  })

  useEffect(() => {
    document.title = `${data?.title || t('routes.dependency-details')} | ${t('routes.accreditations')}`
  }, [data?.title, URL]);
  
  if (isLoading) {
    return (
      <div>
        <div className="w-full h-6 l-load-dt"></div>
        <div className="px-24 py-8 max-lg:px-4">
          <div className="grid lg:grid-cols-[70%_30%] !gap-4 px-3">
            <div className="w-full h-60 l-load-dt rounded-lg"></div>
            <div className="w-full h-60 l-load-dt rounded-lg"></div>
          </div>
          <div className="my-8 space-y-4">
            <div className="h-5 w-48 l-load-dt rounded-md"></div>
            <div className="scrolling gap-4 lg:justify-center">
              {
                [...Array(4)].map((_, i) => (
                  <div key={i} className="h-48 w-52 l-load-dt rounded-md">
                    <div className="w-52"></div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return <p className="text-center text-red-500">{error?.message}</p>;
  }

  const courses = data?.courses || [];

  // Calculate the number of slides to show based on courses length
  const slidesToShow = courses.length > 1 ? Math.min(courses.length, 4) : 1;

  // Slick Slider settings
  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplaySpeed: 3000,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 3 } },
      { breakpoint: 992, settings: { slidesToShow: 2 } },
      { breakpoint: 776, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <>
      <div>
        <Breadcrumb pages={[t('routes.accreditations'), `${data?.title || t('routes.dependency-details') }`]} />
        <div className="container">
          <div className="row justify-content-between " style={{ margin: "40px 0" }}>
            <div className="col-lg-8  first-dependence-description " style={{ marginLeft: '30px' }}>
              <article dangerouslySetInnerHTML={{ __html: data.description }} />
            </div>
            {/* Logo Section */}
            <div className="col-lg-3 first-dependence-image !relative">
              <Image src={data.image} alt={data.title} />
            </div>
          </div>

          {/* Training Programs Section */}
          <div className="dependence-courses">
            <h2>{t('dependencies.title')}</h2>

            {/* Slick Slider */}
            {courses.length > 0 ? (
              <Slider {...settings} className="dependence-courses-slider">
                {courses.map((course) => (
                  <div key={course.id} className="dependence-card">
                    <div style={{ minHeight: '450px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div className="position-relative">
                        <img src={course.image} alt={course.title} />
                        <div className="overlay-img"></div>
                      </div>
                      <span>{course.count_hours} {t('training-courses.hours')}</span>
                      <h4 className="text-lg">{course.title}</h4>
                      <a href={`/training-courses/${course.id}`} className="dependence-card-btn">
                        {t('custom.more-details')} ←
                      </a>
                    </div>
                  </div>
                ))}
              </Slider>
            ) : (
              <p className="text-gray-500 text-center">{t('custom.no-data')}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Custom Next Arrow
const NextArrow = ({ onClick }) => {
  return (
    <div className="max-lg:!hidden absolute top-1/2 right-4 transform -translate-y-1/2 cursor-pointer z-10 d-slick-next">
      <FaChevronRight size={20} className="text-gray-700" onClick={onClick} />
    </div>
  );
};

// Custom Prev Arrow
const PrevArrow = ({ onClick }) => {
  return (
    <div className=" max-lg:!hidden absolute top-1/2 left-4 transform -translate-y-1/2 cursor-pointer z-10 d-slick-prev">
      <FaChevronLeft size={20} className="text-gray-700" onClick={onClick} />
    </div>
  );
};

export default TrainingPrograms;
